import { SortDirection } from 'src/app/services/api.services';
import { VisualChartSortingGroup, VisualChartType } from '../chart-type';
import { VisualChartRoleCategorisation, PbiDataRoles } from '../data-role';
import { SortingGroups } from '../sorting-groups';
import { BarChartVisual, BarChartVisualComponent } from 'src/app/pbir/visuals/highcharts/BarChartVisual.component';

export const BarlikeChartTypes = {
  barChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'barChart',
    alias: 'barChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, // checked
      columns: null, 
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis],
    defaultSortDirection: SortDirection.Descending,
     
    title: 'Bar Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-bar-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-bar-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-bar-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-bar-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true
      },
      values: {
        maxFields: 1,
        replace: true
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        displayType: BarChartVisual.DisplayType.Simple,
      }
    },
  },

  clusteredBarChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'clusteredBarChart',
    alias: 'clusteredBarChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis,
      columns: PbiDataRoles.chartLegend,
      values: PbiDataRoles.chartMeasures
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Descending,
     
    title: 'Clustered Bar Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-bar-clustered-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-bar-clustered-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-bar-clustered-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-bar-clustered-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        })
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        displayType: BarChartVisual.DisplayType.Clustered,
      }
    },
  },

  stackedBarChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'stackedBarChart',
    alias: 'stackedBarChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, // checked
      columns: PbiDataRoles.chartLegend, // checked
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Descending,
     
    title: 'Stacked Bar Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-bar-stacked-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-bar-stacked-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-bar-stacked-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-bar-stacked-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        })
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        displayType: BarChartVisual.DisplayType.Stacked,
      }
    },
  },

  hundredPercentStackedBarChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'hundredPercentStackedBarChart',
    alias: 'hundredPercentStackedBarChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, //checked
      columns: PbiDataRoles.chartLegend, //checked
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Descending,
     
    title: '100% Stacked Bar Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-bar-100-percent-stacked-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-bar-100-percent-stacked-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-bar-100-percent-stacked-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-bar-100-percent-stacked-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        })
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        displayType: BarChartVisual.DisplayType.Stacked100p,
      }
    },
  },
};
